import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-context';
import { createApiClient } from '../lib/api';

const TIERS = ['FREE', 'STARTER', 'PRO', 'EXPERT', 'INTERNAL'] as const;

const tierColors: Record<string, string> = {
  FREE: 'bg-slate-800 text-slate-300',
  STARTER: 'bg-blue-900/50 text-blue-300',
  PRO: 'bg-orange-900/50 text-orange-300',
  EXPERT: 'bg-purple-900/50 text-purple-300',
  INTERNAL: 'bg-green-900/50 text-green-300',
};

const statusColors: Record<string, string> = {
  COMPLETE: 'bg-green-900/50 text-green-300',
  FAILED: 'bg-red-900/50 text-red-300',
  PROCESSING_FAST: 'bg-blue-900/50 text-blue-300',
  PROCESSING_DEEP: 'bg-blue-900/50 text-blue-300',
  NEEDS_FOLLOWUP: 'bg-amber-900/50 text-amber-300',
  UPLOADING: 'bg-slate-800 text-slate-400',
};

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatShortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const { idToken } = useAuth();
  const api = createApiClient(idToken!);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showTierModal, setShowTierModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState('');
  const [customLimit, setCustomLimit] = useState('');
  const [scans, setScans] = useState<any[]>([]);
  const [scanNextToken, setScanNextToken] = useState<string | undefined>();
  const [scanHasMore, setScanHasMore] = useState(false);
  const [isLoadingMoreScans, setIsLoadingMoreScans] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Fetch user detail
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-user-detail', userId],
    queryFn: () => api.getUserDetail(userId!),
    enabled: !!userId,
  });

  // Fetch user scans
  const { data: scansData } = useQuery({
    queryKey: ['admin-user-scans', userId],
    queryFn: () => api.listUserScans(userId!, { limit: 20 }),
    enabled: !!userId,
  });

  useEffect(() => {
    if (scansData) {
      setScans(scansData.scans || []);
      setScanNextToken(scansData.nextToken);
      setScanHasMore(scansData.hasMore || false);
    }
  }, [scansData]);

  // Infinite scroll for scans
  const loadMoreScans = useCallback(async () => {
    if (isLoadingMoreScans || !scanHasMore || !scanNextToken || !userId) return;
    setIsLoadingMoreScans(true);
    try {
      const moreData = await api.listUserScans(userId, { limit: 20, nextToken: scanNextToken });
      setScans((prev) => [...prev, ...(moreData.scans || [])]);
      setScanNextToken(moreData.nextToken);
      setScanHasMore(moreData.hasMore || false);
    } catch {
      // Silently fail
    } finally {
      setIsLoadingMoreScans(false);
    }
  }, [isLoadingMoreScans, scanHasMore, scanNextToken, userId]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && scanHasMore && !isLoadingMoreScans) {
          loadMoreScans();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scanHasMore, isLoadingMoreScans, loadMoreScans]);

  // Mutations
  const updateMutation = useMutation({
    mutationFn: (body: { subscriptionTier?: string; analysesLimit?: number }) =>
      api.updateUser(userId!, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user-detail', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setShowTierModal(false);
      setShowLimitModal(false);
    },
  });

  const user = data?.user;
  const scanStats = data?.scanStats;
  const avgCost = data?.avgCostPerScan ?? 0;

  if (isLoading) {
    return <div className="text-slate-500 py-12 text-center">Loading user...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 text-red-300">
        {(error as Error).message}
      </div>
    );
  }

  if (!user) {
    return <div className="text-slate-500 py-12 text-center">User not found</div>;
  }

  const isUnlimited = user.analysesLimit < 0 || user.analysesLimit >= 999999;

  return (
    <div>
      {/* Back link */}
      <button
        onClick={() => navigate('/users')}
        className="text-sm text-slate-400 hover:text-white mb-4 inline-block"
      >
        &larr; Back to Users
      </button>

      {/* User header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">
            {user.name || user.displayName || 'Unnamed User'}
          </h1>
          <p className="text-slate-400 mt-1">{user.email}</p>
        </div>
        <div className="text-right text-sm text-slate-400">
          <div>Created: {formatDate(user.createdAt)}</div>
          <div>Last Login: {formatDate(user.lastLoginAt)}</div>
          <div>Last Scan: {formatDate(user.lastScanAt)}</div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Tier */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="text-xs text-slate-500 mb-1">Subscription Tier</div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-sm font-medium ${tierColors[user.subscriptionTier] || tierColors.FREE}`}>
              {user.subscriptionTier}
            </span>
          </div>
          <button
            onClick={() => {
              setSelectedTier(user.subscriptionTier);
              setShowTierModal(true);
            }}
            className="text-xs text-orange-500 hover:text-orange-400 mt-2"
          >
            Change Tier
          </button>
        </div>

        {/* Scans */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="text-xs text-slate-500 mb-1">Total Scans</div>
          <div className="text-2xl font-bold text-white">{scanStats?.totalScans ?? 0}</div>
          <div className="text-xs text-slate-500 mt-1">
            {scanStats?.completedScans ?? 0} completed, {scanStats?.failedScans ?? 0} failed
          </div>
        </div>

        {/* Est. Cost */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="text-xs text-slate-500 mb-1">Est. Total Cost</div>
          <div className="text-2xl font-bold text-white">
            ${(scanStats?.estimatedTotalCost ?? 0).toFixed(2)}
          </div>
          <div className="text-xs text-slate-500 mt-1">~${avgCost.toFixed(2)}/scan avg</div>
        </div>

        {/* Analyses Remaining */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="text-xs text-slate-500 mb-1">Analyses Remaining</div>
          <div className="text-2xl font-bold text-white">
            {isUnlimited ? (
              <span className="text-green-400">Unlimited</span>
            ) : (
              <>
                {user.analysesRemaining}
                <span className="text-base text-slate-500 font-normal">/{user.analysesLimit}</span>
              </>
            )}
          </div>
          <button
            onClick={() => {
              setCustomLimit(String(user.analysesLimit));
              setShowLimitModal(true);
            }}
            className="text-xs text-orange-500 hover:text-orange-400 mt-2"
          >
            Set Custom Limit
          </button>
        </div>
      </div>

      {/* Additional info */}
      {(user.referralCode || user.referredBy) && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 mb-6 text-sm">
          {user.referralCode && (
            <span className="text-slate-400 mr-6">
              Referral Code: <span className="text-white font-mono">{user.referralCode}</span>
            </span>
          )}
          {user.referredBy && (
            <span className="text-slate-400">
              Referred By: <Link to={`/users/${user.referredBy}`} className="text-orange-500 hover:text-orange-400">{user.referredBy}</Link>
            </span>
          )}
        </div>
      )}

      {/* Scan History */}
      <h2 className="text-lg font-semibold text-white mb-3">Scan History</h2>
      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800">
              <th className="text-left px-4 py-3 text-slate-400 font-medium">Session ID</th>
              <th className="text-left px-4 py-3 text-slate-400 font-medium">Category</th>
              <th className="text-left px-4 py-3 text-slate-400 font-medium">Status</th>
              <th className="text-right px-4 py-3 text-slate-400 font-medium">Est. Cost</th>
              <th className="text-left px-4 py-3 text-slate-400 font-medium">Created</th>
              <th className="text-left px-4 py-3 text-slate-400 font-medium">Completed</th>
            </tr>
          </thead>
          <tbody>
            {scans.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-8 text-slate-500">
                  No scans found
                </td>
              </tr>
            )}
            {scans.map((scan: any) => (
              <tr
                key={scan.scanSessionId}
                onClick={() => navigate(`/scans/${scan.scanSessionId}`)}
                className="border-b border-slate-800/50 hover:bg-slate-800/50 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 text-slate-300 font-mono text-xs">
                  {scan.scanSessionId.substring(0, 8)}...
                </td>
                <td className="px-4 py-3 text-slate-300">
                  {scan.resolvedCategory || scan.categoryHint || '-'}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[scan.status] || 'bg-slate-800 text-slate-400'}`}>
                    {scan.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-slate-400">
                  {scan.estimatedCost != null ? `~$${scan.estimatedCost.toFixed(2)}` : '-'}
                </td>
                <td className="px-4 py-3 text-slate-400">{formatShortDate(scan.createdAt)}</td>
                <td className="px-4 py-3 text-slate-400">{formatShortDate(scan.completedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-4" />
        {isLoadingMoreScans && (
          <div className="text-center py-4 text-slate-500 text-sm">Loading more...</div>
        )}
      </div>

      {/* Change Tier Modal */}
      {showTierModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-96">
            <h3 className="text-lg font-semibold text-white mb-4">Change Subscription Tier</h3>
            <p className="text-sm text-slate-400 mb-4">
              This will reset the user's analyses limit and remaining count to the new tier's defaults.
            </p>
            <div className="flex flex-col gap-2 mb-4">
              {TIERS.map((t) => (
                <button
                  key={t}
                  onClick={() => setSelectedTier(t)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium text-left transition-colors ${
                    selectedTier === t
                      ? 'bg-orange-600 text-white'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            {updateMutation.error && (
              <div className="text-red-400 text-sm mb-3">{(updateMutation.error as Error).message}</div>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowTierModal(false)}
                className="px-4 py-2 text-sm text-slate-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => updateMutation.mutate({ subscriptionTier: selectedTier })}
                disabled={updateMutation.isPending || selectedTier === user.subscriptionTier}
                className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateMutation.isPending ? 'Updating...' : 'Confirm Change'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Limit Modal */}
      {showLimitModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-96">
            <h3 className="text-lg font-semibold text-white mb-4">Set Custom Analyses Limit</h3>
            <p className="text-sm text-slate-400 mb-4">
              This will reset the user's remaining analyses to the new limit. Use -1 for unlimited.
            </p>
            <input
              type="number"
              value={customLimit}
              onChange={(e) => setCustomLimit(e.target.value)}
              min={-1}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500 mb-4"
              placeholder="e.g., 25 or -1 for unlimited"
            />
            {updateMutation.error && (
              <div className="text-red-400 text-sm mb-3">{(updateMutation.error as Error).message}</div>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowLimitModal(false)}
                className="px-4 py-2 text-sm text-slate-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const limit = parseInt(customLimit, 10);
                  if (!isNaN(limit)) {
                    updateMutation.mutate({ analysesLimit: limit });
                  }
                }}
                disabled={updateMutation.isPending}
                className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-500 disabled:opacity-50"
              >
                {updateMutation.isPending ? 'Updating...' : 'Set Limit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
